// DraftPhotoPreviews (#1493, #1494): draft-scoped registry of photo
// previews keyed by storage object key. Photo thumbnails were stored as
// component-LOCAL state, so duplicating a position (a fresh component
// instance that copies only the storage keys) showed broken-image
// placeholders even though the photos were attached. Hoisting the preview
// URLs into one draft-scoped registry lets a clone read its source's
// preview by KEY before any save.
//
// Two preview sources share the registry, both keyed by object key:
//   - session BLOB URLs for photos uploaded in the current session (#1493)
//   - signed DOWNLOAD URLs for photos that come from an already-saved
//     request, fetched once per key through a persisted "authorizer" (a
//     requestId + persisted itemId that already owns the key) (#1494)
//
// @aiPurpose Share both current-session blob previews and saved-photo
//   signed-download previews across every position row of one draft,
//   keyed by storage object key, so a cloned position renders the same
//   preview as its source before the clone's own id is persisted.
// @aiInvariant Preview URLs are ref-counted by the draft's active keys,
//   not by component instances: a key referenced by source + clone
//   survives until NEITHER lists it. `reconcile` drops blob URLs (with
//   revoke), download URLs, authorizers, and the requested-set for keys
//   absent from every position; provider unmount revokes all blob URLs.
// @aiInvariant At most ONE signed-download fetch per key: a key enters
//   `requestedKeysRef` when its fetch is initiated and is never refetched
//   (success caches the URL; an error silently falls back to the
//   placeholder) until `reconcile` drops the key.
// @aiSecurity Signed download URLs are bearer-like temporary access URLs;
//   they live ONLY in in-memory refs here. Never log them, never put them
//   in storage/URLs/telemetry. The only fetch is the existing
//   `requestItemPhotoDownloadUrl` through a persisted authorizer that
//   already owns the key; this adds no new backend surface or access.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { StorageDownloadUrlResponse } from '@magimp/shared';
import { requestItemPhotoDownloadUrl } from '../../../api/requests/index.js';

/**
 * Resolve a saved position photo's signed download URL by
 * `(requestId, itemId, objectKey)`. Defaults to the China
 * `requestItemPhotoDownloadUrl`; Russia SIMPLE (#1545) mounts the same
 * provider with its own request-scoped resolver so one registry serves
 * both without duplicating the fetch/cache/revoke machinery.
 */
export type FetchDownloadUrl = (
  requestId: string,
  itemId: string,
  objectKey: string,
) => Promise<StorageDownloadUrlResponse>;

interface Authorizer {
  requestId: string;
  itemId: string;
}

export interface DraftPhotoPreviewsApi {
  /** Registered session BLOB URL for the key, or null. */
  getBlobUrl: (objectKey: string) => string | null;
  /**
   * Best preview URL for the key: the session blob if present, else the
   * cached signed download URL, else null.
   */
  getPreviewUrl: (objectKey: string) => string | null;
  /**
   * Mint a blob URL for the key from `file` if none exists yet.
   * Idempotent: a second call for the same key does NOT create a second
   * URL.
   */
  registerBlob: (objectKey: string, file: File) => void;
  /**
   * Record a persisted authorizer (requestId + persisted itemId that owns
   * the key) so the registry can fetch the key's signed download URL.
   * First registration wins. Callers MUST pass a persisted `it-NNNN`
   * itemId, a non-null requestId, and an issued-shaped key.
   */
  registerAuthorizer: (objectKey: string, requestId: string, itemId: string) => void;
  /**
   * Ask the registry to resolve the key's signed download URL. Fetches
   * once when an authorizer exists and no blob/download URL is cached yet;
   * on success caches the URL, on error leaves it null. Safe to call every
   * render from an effect: a no-op once the fetch has been initiated.
   */
  requestKey: (objectKey: string) => void;
  /**
   * Revoke + drop every registered key NOT present in `activeKeys`.
   * `activeKeys` is the union across ALL positions, so a key shared by
   * source + clone survives until neither references it.
   */
  reconcile: (activeKeys: readonly string[]) => void;
}

// WHY (D6): a stable no-op default so `useDraftPhotoPreviews()` is safe
//   OUTSIDE the provider. The many standalone row/strip tests render
//   without the provider and must not crash.
const NOOP_API: DraftPhotoPreviewsApi = {
  getBlobUrl: () => null,
  getPreviewUrl: () => null,
  registerBlob: () => undefined,
  registerAuthorizer: () => undefined,
  requestKey: () => undefined,
  reconcile: () => undefined,
};

const DraftPhotoPreviewsContext = createContext<DraftPhotoPreviewsApi>(NOOP_API);

export function useDraftPhotoPreviews(): DraftPhotoPreviewsApi {
  return useContext(DraftPhotoPreviewsContext);
}

export interface DraftPhotoPreviewsProviderProps {
  children: ReactNode;
  /**
   * Resolver for saved-photo signed download URLs. Defaults to the China
   * `requestItemPhotoDownloadUrl` so every existing China mount site keeps
   * working unchanged; Russia SIMPLE (#1545) passes its own
   * request-scoped resolver.
   */
  fetchDownloadUrl?: FetchDownloadUrl;
}

export function DraftPhotoPreviewsProvider({
  children,
  fetchDownloadUrl = requestItemPhotoDownloadUrl,
}: DraftPhotoPreviewsProviderProps) {
  // Refs are the source of truth so the create/revoke/fetch side effects
  // stay OUT of any state updater (an impure updater would mint a blob
  // twice under React's double-invoke). A version counter forces consumer
  // rerenders after a mutation so a freshly-cloned position that already
  // lists the key picks up the preview.
  const blobByKeyRef = useRef<Record<string, string>>({});
  const downloadByKeyRef = useRef<Record<string, string>>({});
  const authorizerByKeyRef = useRef<Record<string, Authorizer>>({});
  const requestedKeysRef = useRef<Set<string>>(new Set());
  // WHY (#1545): read the resolver through a ref so `requestKey` keeps a
  //   stable identity (effects call it every render) while still using the
  //   latest `fetchDownloadUrl`. China sites pass nothing → the default.
  const fetchDownloadUrlRef = useRef<FetchDownloadUrl>(fetchDownloadUrl);
  fetchDownloadUrlRef.current = fetchDownloadUrl;
  const [version, bump] = useState(0);
  const rerender = useCallback(() => bump((v) => v + 1), []);

  const getBlobUrl = useCallback(
    (objectKey: string) => blobByKeyRef.current[objectKey] ?? null,
    [],
  );

  const getPreviewUrl = useCallback(
    (objectKey: string) =>
      blobByKeyRef.current[objectKey] ?? downloadByKeyRef.current[objectKey] ?? null,
    [],
  );

  const registerBlob = useCallback(
    (objectKey: string, file: File) => {
      if (blobByKeyRef.current[objectKey]) return;
      const url = createObjectUrl(file);
      if (!url) return;
      blobByKeyRef.current[objectKey] = url;
      rerender();
    },
    [rerender],
  );

  const registerAuthorizer = useCallback((objectKey: string, requestId: string, itemId: string) => {
    if (authorizerByKeyRef.current[objectKey]) return;
    authorizerByKeyRef.current[objectKey] = { requestId, itemId };
  }, []);

  const requestKey = useCallback(
    (objectKey: string) => {
      if (requestedKeysRef.current.has(objectKey)) return;
      if (blobByKeyRef.current[objectKey] || downloadByKeyRef.current[objectKey]) return;
      const auth = authorizerByKeyRef.current[objectKey];
      if (!auth) return;
      requestedKeysRef.current.add(objectKey);
      fetchDownloadUrlRef
        .current(auth.requestId, auth.itemId, objectKey)
        .then((res) => {
          // INVARIANT: a reconcile may have dropped this key while the
          // fetch was in flight (no active position references it any
          // more). reconcile clears the authorizer for an absent key, so
          // its absence means the draft no longer holds the key - do NOT
          // store a bearer-like signed URL for it (#1494 AC-4/D4).
          if (!authorizerByKeyRef.current[objectKey]) return;
          downloadByKeyRef.current[objectKey] = res.downloadUrl;
          rerender();
        })
        .catch(() => {
          // Silent fallback to the placeholder; the key stays in
          // requestedKeysRef so we never refetch.
        });
    },
    [rerender],
  );

  const reconcile = useCallback(
    (activeKeys: readonly string[]) => {
      const present = new Set(activeKeys);
      let changed = false;
      for (const key of Object.keys(blobByKeyRef.current)) {
        if (!present.has(key)) {
          revokeObjectUrl(blobByKeyRef.current[key]!);
          delete blobByKeyRef.current[key];
          changed = true;
        }
      }
      for (const key of Object.keys(downloadByKeyRef.current)) {
        if (!present.has(key)) {
          delete downloadByKeyRef.current[key];
          changed = true;
        }
      }
      for (const key of Object.keys(authorizerByKeyRef.current)) {
        if (!present.has(key)) delete authorizerByKeyRef.current[key];
      }
      for (const key of [...requestedKeysRef.current]) {
        if (!present.has(key)) requestedKeysRef.current.delete(key);
      }
      if (changed) rerender();
    },
    [rerender],
  );

  // Revoke every owned blob URL on provider unmount. Download URLs are
  // plain signed strings (not object URLs) and need no revoke.
  useEffect(() => {
    return () => {
      for (const url of Object.values(blobByKeyRef.current)) revokeObjectUrl(url);
    };
  }, []);

  // WHY: `version` is a memo dep so the context value gets a fresh
  //   identity after each mutation, propagating the rerender to consumers
  //   that read URLs through `getBlobUrl` / `getPreviewUrl`.
  const api = useMemo<DraftPhotoPreviewsApi>(
    () => ({ getBlobUrl, getPreviewUrl, registerBlob, registerAuthorizer, requestKey, reconcile }),
    [getBlobUrl, getPreviewUrl, registerBlob, registerAuthorizer, requestKey, reconcile, version],
  );

  return (
    <DraftPhotoPreviewsContext.Provider value={api}>{children}</DraftPhotoPreviewsContext.Provider>
  );
}

function createObjectUrl(file: File): string | null {
  return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    ? URL.createObjectURL(file)
    : null;
}

function revokeObjectUrl(url: string): void {
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}
